import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Fetch the binding registry catalogue for an asset class. Returns the
 * raw rows so callers can display label + description + scope.
 *
 * Cache: per-(brand,asset_class) in-memory map — registries change rarely
 * and authors keep the form open while toggling pills; refetching every
 * keystroke is wasteful.
 */
const _cache = new Map();

export default function useBindings(assetClass, brandId) {
  const key = `${brandId || "_global"}::${assetClass}`;
  const [bindings, setBindings] = useState(() => _cache.get(key) || []);
  const [loading, setLoading] = useState(!_cache.has(key));

  useEffect(() => {
    let cancelled = false;
    if (_cache.has(key)) {
      setBindings(_cache.get(key));
      setLoading(false);
      return;
    }
    setLoading(true);
    api
      .get(`/brand-kit/bindings`, {
        params: { asset_class: assetClass, brand_id: brandId },
      })
      .then(({ data }) => {
        if (cancelled) return;
        _cache.set(key, data);
        setBindings(data);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [key, assetClass, brandId]);

  return { bindings, loading };
}

// Bust the cache (e.g. after the registry is mutated elsewhere).
export function invalidateBindingsCache(brandId, assetClass) {
  if (brandId && assetClass) {
    _cache.delete(`${brandId}::${assetClass}`);
  } else {
    _cache.clear();
  }
}
